export type LinkifiedTextPart =
  | { readonly type: "text"; readonly value: string }
  | { readonly type: "link"; readonly href: string; readonly value: string };

const HTTP_URL_PATTERN = /https?:\/\/[^\s<>"'`]+/gi;

const TRAILING_SENTENCE_PUNCTUATION = new Set([".", ",", "!", "?", ":", ";"]);

const TRAILING_CLOSER_TO_OPENER: Readonly<Record<string, string>> = {
  ")": "(",
  "]": "[",
  "}": "{",
};

function countChar(value: string, char: string): number {
  let count = 0;
  for (const current of value) {
    if (current === char) count += 1;
  }
  return count;
}

function trimTrailingUrlPunctuation(rawUrl: string): {
  url: string;
  trailing: string;
} {
  let end = rawUrl.length;
  while (end > 0) {
    const last = rawUrl[end - 1];
    if (!last) break;
    if (TRAILING_SENTENCE_PUNCTUATION.has(last)) {
      end -= 1;
      continue;
    }
    const opener = TRAILING_CLOSER_TO_OPENER[last];
    if (opener) {
      const prefix = rawUrl.slice(0, end);
      if (countChar(prefix, last) > countChar(prefix, opener)) {
        end -= 1;
        continue;
      }
    }
    break;
  }
  return { url: rawUrl.slice(0, end), trailing: rawUrl.slice(end) };
}

function isSafeHttpUrl(candidate: string): boolean {
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/** Split plain text into text and http(s) link parts without mutating the source. */
export function splitLinkifiedText(text: string): LinkifiedTextPart[] {
  if (text.length === 0) {
    return [];
  }

  const parts: LinkifiedTextPart[] = [];
  let cursor = 0;
  HTTP_URL_PATTERN.lastIndex = 0;

  for (const match of text.matchAll(HTTP_URL_PATTERN)) {
    const raw = match[0];
    const start = match.index;
    if (raw == null || start == null) continue;

    const { url, trailing } = trimTrailingUrlPunctuation(raw);
    if (!isSafeHttpUrl(url)) continue;

    if (start > cursor) {
      parts.push({ type: "text", value: text.slice(cursor, start) });
    }
    parts.push({ type: "link", href: url, value: url });
    cursor = start + raw.length - trailing.length;
  }

  if (cursor < text.length) {
    parts.push({ type: "text", value: text.slice(cursor) });
  }

  return parts;
}
